import jsPDF from 'jspdf';

// Render the backend's edit-instructions into a downloadable PDF for the external
// editor.
//
// The instructions are PLAIN TEXT laid out with real typography — rules, aligned
// label columns, a fixed-width track diagram, and hard wrapping at a fixed column
// (see instructionsRenderer.js). So this prints them MONOSPACE and verbatim rather
// than re-flowing them: the PDF then matches the on-screen copy box line for line,
// and the track diagram — which is only legible when every character is the same
// width — finally survives the trip. Reflowing it in a proportional face is what
// used to mangle it.
//
// The only styling applied is weight: the title inside the `═` banner and each
// section heading (the line directly above a `─` rule) are bold, so the structure
// is still scannable at a glance.
const RULE = /^[═─]+$/;

// Courier advances every glyph by 0.6 em, so the columns that fit on a page are
// exact rather than measured.
const COURIER_ADVANCE = 0.6;

// Split a line that is wider than the page into page-width pieces, carrying its
// indent onto each continuation. Only the renderer's unbreakable tokens (a signed
// preview URL) ever hit this — everything else is already wrapped well inside it.
function fitToWidth(line: string, columns: number): string[] {
  if (line.length <= columns) return [line];
  const indent = line.match(/^\s*/)?.[0] ?? '';
  const pieces: string[] = [line.slice(0, columns)];
  let rest = line.slice(columns);
  // A continuation must leave room for its own indent, or an indented over-long
  // line would never shorten and this would not terminate.
  const chunk = Math.max(1, columns - indent.length);
  while (rest.length > 0) {
    pieces.push(indent + rest.slice(0, chunk));
    rest = rest.slice(chunk);
  }
  return pieces;
}

export function generateInstructionsPdf(text: string, filename = 'edit-instructions.pdf') {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const fontSize = 9;
  const lineHeight = fontSize + 2.6;
  const columns = Math.floor((pageW - margin * 2) / (fontSize * COURIER_ADVANCE));
  let y = margin + fontSize;

  const lines = text.split('\n');
  // A line is a heading when the next one is a rule; a line is the document title
  // when it sits between two `═` rules. Both are decided up front so the drawing
  // loop stays a straight pass.
  const isRule = lines.map((l) => RULE.test(l.trim()));
  const isBold = lines.map((line, i) => !!line.trim() && (isRule[i + 1] || isRule[i - 1]) && !isRule[i]);

  lines.forEach((line, i) => {
    doc.setFont('courier', isBold[i] ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(isRule[i] ? 150 : 30);
    // Written verbatim (bar an over-long URL): the renderer already wrapped and
    // indented every line, so re-wrapping here would fight it — and would break the
    // diagram, which only lines up if its rows are left exactly as built.
    for (const piece of fitToWidth(line, columns)) {
      if (y > pageH - margin) { doc.addPage(); y = margin + fontSize; }
      if (piece.trim()) doc.text(piece, margin, y);
      y += lineHeight;
    }
  });

  doc.save(filename);
}
